import { useState } from 'react';
import { Check } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { CullBadges } from '@/components/culling/CullBadges';
import { thumbUrl, displayName } from '@/components/culling/cullUtils';
import { CULL } from '@/constants/testIds';

const Thumb = ({ image, index, selected, onToggle }) => {
  const [loaded, setLoaded] = useState(false);
  const c = image.cull || {};
  return (
    <button
      type="button"
      data-testid={CULL.card(image.id)}
      onClick={() => onToggle(image.id)}
      aria-pressed={selected}
      className={`group relative flex flex-col overflow-hidden rounded-xl border bg-[#0F0F10] text-left transition-[transform,border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold))] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A] active:scale-[0.99] ${
        selected ? 'border-[hsl(var(--lxra-gold))] shadow-[0_0_0_1px_rgba(201,164,92,0.35)]' : 'border-[hsl(var(--lxra-border))] hover:border-[hsl(var(--lxra-gold))]/50'
      } ${image.hidden ? 'opacity-70' : ''}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#141416]">
        {!loaded && <Skeleton className="absolute inset-0 rounded-none bg-[#141416]" />}
        <img
          src={thumbUrl(image)}
          alt={displayName(image, index)}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`h-full w-full object-cover transition-[transform,opacity] duration-500 group-hover:scale-[1.04] ${loaded ? 'opacity-100' : 'opacity-0'} ${image.hidden ? 'grayscale' : ''}`}
        />
        <span
          data-testid={CULL.cardCheckbox(image.id)}
          className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${
            selected ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]' : 'border-white/50 bg-black/50 text-transparent group-hover:border-white'
          }`}
        >
          <Check className="h-3.5 w-3.5" />
        </span>
        <div className="absolute inset-x-2 bottom-2"><CullBadges image={image} compact /></div>
      </div>
      <div className="flex items-center justify-between gap-2 px-2.5 py-2">
        <p className="truncate text-[11px] text-[hsl(var(--lxra-fg))]">{displayName(image, index)}</p>
        {c.analyzed && c.sharpness != null && (
          <span data-testid={CULL.sharpness(image.id)} title="Sharpness score (higher is crisper)" className={`shrink-0 font-mono text-[10px] ${c.blurry ? 'text-red-300' : 'text-[hsl(var(--lxra-muted))]'}`}>{Math.round(c.sharpness)}</span>
        )}
      </div>
      {c.note && (
        <p data-testid={CULL.note(image.id)} className="px-2.5 pb-2 text-[10px] leading-snug text-[hsl(var(--lxra-muted))] line-clamp-2">{c.note}</p>
      )}
    </button>
  );
};

export const CullGrid = ({ images, selectedIds, onToggle }) => (
  <div data-testid={CULL.grid} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
    {images.map((img, i) => <Thumb key={img.id} image={img} index={i} selected={selectedIds.has(img.id)} onToggle={onToggle} />)}
  </div>
);
